import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatTime, localToUtc, todayISO, tzOffsetMinutes, type TimeFormat } from '../../lib/time';
import { hoursSummary } from '../../lib/org';
import { plural } from '../../lib/plural';
import { ChevronIcon } from '../../ui/icons';
import { Field, FormSkeleton, LoadFailed, StickySave, useSaveOrLeave } from '../org-parts';
import { useOrgDraft } from '../org-draft';
import { useAdminSettings, useSaveSettings } from '../api';

/** Часовые пояса, в которых реально работают организации. */
export const ZONES = [
  { tz: 'Europe/Kaliningrad', title: 'Калининград · UTC+2' },
  { tz: 'Europe/Moscow', title: 'Москва · UTC+3' },
  { tz: 'Europe/Samara', title: 'Самара · UTC+4' },
  { tz: 'Asia/Yekaterinburg', title: 'Екатеринбург · UTC+5' },
  { tz: 'Asia/Omsk', title: 'Омск · UTC+6' },
  { tz: 'Asia/Novosibirsk', title: 'Новосибирск · UTC+7' },
  { tz: 'Asia/Irkutsk', title: 'Иркутск · UTC+8' },
  { tz: 'Asia/Yakutsk', title: 'Якутск · UTC+9' },
  { tz: 'Asia/Vladivostok', title: 'Владивосток · UTC+10' },
  { tz: 'Asia/Kamchatka', title: 'Камчатка · UTC+12' },
];

/** Город пояса для сводки: «Москва · UTC+3» в строке-переходе избыточен. */
export function zoneLabel(tz: string): string {
  const found = ZONES.find((z) => z.tz === tz);
  return found ? found.title.split(' · ')[0] : tz;
}

const CANCEL_HOURS = [0, 2, 4, 12, 24, 48, 72];

type Draft = { tz: string; time_format: TimeFormat; cancel_hours: number };

/**
 * Раздел «Расписание и запись»: в каком времени живёт организация и по каким
 * правилам её записывают.
 *
 * Часы работы правятся своим подэкраном и своей кнопкой — это отдельная запись
 * в базе (JSON-колонка), и сохранять её вместе с поясом значило бы отправлять
 * на сервер то, чего не трогали.
 */
export function OrgSchedule() {
  const writable = useWritable();
  const navigate = useNavigate();
  const toast = useToast();
  const data = useAdminSettings();
  const save = useSaveSettings();

  const s = data.data?.settings;
  const { draft, base, dirty, patch, commit } = useOrgDraft<Draft>(s
    ? { tz: s.tz, time_format: s.time_format, cancel_hours: s.cancel_hours }
    : null);

  useScreen('Расписание и запись', null);
  useSaveOrLeave(dirty, () => submit());

  if (data.isError) {
    return <Screen><LoadFailed busy={data.isFetching} onRetry={() => void data.refetch()} /></Screen>;
  }
  if (!draft || !base || !data.data) {
    return (
      <Screen>
        <FormSkeleton rows={4} />
      </Screen>
    );
  }

  const { hours } = data.data;
  const busy = save.isPending;
  const locked = !writable || busy;

  function submit(): Promise<unknown> {
    if (!draft) return Promise.resolve();
    if (!draft.tz) {
      toast('Без часового пояса не посчитать расписание');
      return Promise.reject(new Error('VALIDATION'));
    }
    // Уходят только поля этого раздела: PATCH принимает частичную правку, и
    // профиль с уведомлениями остаются такими, какие они на сервере.
    return save.mutateAsync(draft).then(
      () => {
        commit(draft);
        toast('Изменения сохранены', { ms: 2000 });
      },
      (e: unknown) => {
        toast(e instanceof ApiError ? e.message : 'Не получилось сохранить');
        throw e;
      },
    );
  }

  return (
    <Screen className="orgform sticky-save">
      <p className="lead">В каком времени живёт организация и по каким правилам её записывают</p>

      <div className="kv kv-wrap">
        <button className="kv-row" type="button" onClick={() => navigate('/admin/org/hours')}>
          <span className="k">Часы работы</span>
          <span className="v">
            <span className="chev"><ChevronIcon /></span>
          </span>
        </button>
        <p className="kv-note">{hoursSummary(hours, draft.time_format)}</p>
      </div>

      <Field
        label="Часовой пояс"
        hint={tzHint(base.tz, draft.tz, draft.time_format)}
      >
        {(id) => (
          <select
            id={id} value={draft.tz} disabled={locked}
            onChange={(e) => patch({ tz: e.target.value })}
          >
            {ZONES.map((z) => <option key={z.tz} value={z.tz}>{z.title}</option>)}
            {ZONES.some((z) => z.tz === draft.tz) ? null : <option value={draft.tz}>{draft.tz}</option>}
          </select>
        )}
      </Field>

      {/* Формат времени стоит сразу за поясом: обе настройки отвечают на один вопрос —
          как организация видит время. Пояс задаёт, КАКОЕ время показать, формат — КАК. */}
      <Field
        label="Формат времени"
        hint="Используется в приложении и сообщениях бота."
      >
        {(id) => (
          <select
            id={id} value={draft.time_format} disabled={locked}
            onChange={(e) => patch({ time_format: e.target.value as TimeFormat })}
          >
            <option value="24">24 часа · 14:30</option>
            <option value="12">12 часов · 2:30 PM</option>
          </select>
        )}
      </Field>

      <div className="kv kv-wrap">
        {/* Подпись самодостаточна и потому длинна — списку значений рядом с ней
            места не остаётся. Ставим его под подписью, во всю ширину. */}
        <label className="kv-row stack">
          <span className="k">Отмена без штрафа за</span>
          <span className="v">
            <select
              aria-label="Отмена без штрафа за"
              value={draft.cancel_hours}
              disabled={locked}
              onChange={(e) => patch({ cancel_hours: Number(e.target.value) })}
            >
              {CANCEL_HOURS.map((h) => (
                <option key={h} value={h}>
                  {h === 0 ? 'без ограничения' : `${h} ${plural(h, 'час', 'часа', 'часов')}`}
                </option>
              ))}
            </select>
          </span>
        </label>
        <p className="kv-note">
          Позже этого срока клиент отменяет запись уже со штрафом. «Без ограничения» — отменять
          можно в любой момент.
        </p>
      </div>

      {writable ? (
        <StickySave dirty={dirty} busy={busy} failed={save.isError} onClick={() => { void submit().catch(() => {}); }} />
      ) : null}
    </Screen>
  );
}

/**
 * Что будет с уже созданными записями при смене пояса. Они хранятся в UTC и не
 * сдвигаются, но показываться начнут по-новому — и это надо увидеть ДО
 * сохранения, иначе администратор узнает о сдвиге от клиентов.
 */
function tzHint(current: string, next: string, format: TimeFormat): string {
  if (next === current) return 'Время записей указано в этом часовом поясе.';
  const sample = localToUtc(todayISO(current), 16 * 60, current);
  const deltaMin = tzOffsetMinutes(next, sample) - tzOffsetMinutes(current, sample);
  if (deltaMin === 0) return 'Смещение то же — время записей не изменится';
  const h = Math.round(deltaMin / 60);
  return `Запись, показанная как ${formatTime(sample, current, format)}, станет ${formatTime(sample, next, format)}`
    + ` (${h > 0 ? '+' : ''}${h} ${plural(Math.abs(h), 'час', 'часа', 'часов')}) — у специалистов и клиентов тоже`;
}
